import { useState, useRef, useEffect } from 'react';

/**
 * Lazy-loaded image with skeleton placeholder.
 * - Uses IntersectionObserver to only load when near viewport
 * - Shows pulsing skeleton while loading
 * - For Cloudinary URLs: adds on-the-fly resize/format transforms
 * - For legacy local URLs: uses /api/uploads/thumb/ endpoint with fallback
 */
export default function OptimizedImage({ src, alt = '', className = '', style = {}, thumbWidth = 400, onClick }) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const optimizedSrc = (() => {
    if (!src) return '';
    if (useFallback) return src;

    // Cloudinary URL — add on-the-fly transforms
    if (src.includes('res.cloudinary.com')) {
      return src.replace('/upload/', `/upload/w_${thumbWidth},q_auto,f_auto/`);
    }

    // Legacy local URL — try thumbnail endpoint
    if (src.startsWith('/api/uploads/')) {
      const filename = src.replace('/api/uploads/', '');
      if (filename.includes('_vid.') || filename.startsWith('_thumbs/')) return src;
      return `/api/uploads/thumb/${filename}?w=${thumbWidth}`;
    }

    return src;
  })();

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} style={style} onClick={onClick}>
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse"
          style={{ background: 'linear-gradient(110deg, #111 30%, #1a1a1a 50%, #111 70%)', backgroundSize: '200% 100%' }}
        />
      )}
      {inView && optimizedSrc && (
        <img
          src={optimizedSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            if (!useFallback) {
              setUseFallback(true);
              setLoaded(false);
            }
          }}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
}
